/**
 * PotPicksModal — iter87.5. Brutalist-centered dialog matching the
 * Power Up shell. Header carries the same Live / Open / Locked status
 * pill the chip wore, and the modal auto-dismisses after a successful
 * save so the chip immediately reflects the new Open / Locked state.
 * iter87.59 — motion via shared BentoModal.
 * iter87.406 (user-requested) — floating header/footer shell to match
 * the Matchday Boost modal (iter87.380): header sits flush against the
 * top border, content scrolls independently, and the save footer
 * (rendered by the child panel via this portal slot) sits flush
 * against the bottom border instead of scrolling with the picks.
 */
import { useState } from "react";
import { ChevronRight, ListChecks } from "lucide-react";
import SeasonPotPickPanel from "@/components/dashboard/SeasonPotPickPanel";
import GolfPickPanel from "@/components/dashboard/GolfPickPanel";
import { isGolfPickem } from "@/lib/tournamentFormat";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";
import { todoStatusFor } from "@/lib/todoStatus";
import ChipStatusPill from "@/components/onui/ChipStatusPill";
import { BentoModal } from "@/components/onui/BentoMotion";

export default function PotPicksModal({
    open, onOpenChange, tournament, me, board, isLeagueAdmin, onPicked,
}) {
    const [footerSlot, setFooterSlot] = useState(null);
    if (!tournament || !me) return null;
    const isGolf = isGolfPickem(tournament);
    const title = isGolf ? "Your Golfer Picks" : isFootballPlayoffsTemplate(tournament?.template) ? "Your Playoff Picks" : "Your Season Picks";
    const status = todoStatusFor("pot-picks", { tournament, me, board });

    // Chip Save Autoclose (iter87.5): fire parent refresh, then dismiss.
    const handleSaved = () => {
        onPicked?.();
        setTimeout(() => onOpenChange?.(false), 220);
    };

    return (
        <BentoModal
            open={open}
            onClose={() => onOpenChange?.(false)}
            testId="pot-picks-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card max-w-3xl w-full max-h-[90dvh] overflow-hidden border-cyber flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.08] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-8 h-8 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <ListChecks className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate min-w-0">{title}</h3>
                    <ChipStatusPill status={status} testId="pot-picks-modal-status" />
                </div>
                <button
                    onClick={() => onOpenChange?.(false)}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-pot-picks-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                {isGolf ? (
                    <GolfPickPanel
                        tournament={tournament}
                        me={me}
                        hideCollapse
                        onPicked={handleSaved}
                        footerSlot={footerSlot}
                    />
                ) : (
                    <SeasonPotPickPanel
                        tournament={tournament}
                        me={me}
                        isLeagueAdmin={isLeagueAdmin}
                        hideCollapse
                        onPicked={handleSaved}
                        footerSlot={footerSlot}
                    />
                )}
            </div>
            <div ref={setFooterSlot} className="shrink-0" />
        </BentoModal>
    );
}
